import {
    Box,
    Card,
    Grid,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Slider,
    TextField,
    CircularProgress,
    Button,
    ButtonGroup,
    useTheme,
    Tooltip as MuiTooltip,
    Paper,
    Checkbox
} from '@mui/material';
import NumbersIcon from '@mui/icons-material/Numbers';
import { MetricRule, MetricRuleSet, RuleGroup, extractAvailableFields } from '../calculation';

// React component for metric rules UI
interface MetricRulesProps {
    timeseriesData: any[];
    metricRules: MetricRuleSet;
    onRulesChange: (rules: MetricRuleSet) => void;
}

export const MetricRules: React.FC<MetricRulesProps> = ({ timeseriesData, metricRules, onRulesChange }) => {
    const availableFields = extractAvailableFields(timeseriesData);

    const operatorOptions = [
        { value: 'gt', label: '>' },
        { value: 'lt', label: '<' },
        { value: 'gte', label: '>=' },
        { value: 'lte', label: '<=' },
        { value: 'eq', label: '==' },
        { value: 'neq', label: '!=' },
    ];

    const addRuleGroup = () => {
        const newGroup: RuleGroup = {
            id: Date.now().toString(),
            rules: [],
            logic: 'OR'
        };
        onRulesChange({
            ...metricRules,
            groups: [...metricRules.groups, newGroup]
        });
    };

    const addRule = (groupId: string) => {
        const newRule: MetricRule = {
            id: Date.now().toString(),
            field: availableFields[0] || '',
            operator: 'gt',
            threshold: 0,
            allocationFactor: 1.0,
            enabled: true,
        };

        const updatedGroups = metricRules.groups.map(group =>
            group.id === groupId
                ? { ...group, rules: [...group.rules, newRule] }
                : group
        );

        onRulesChange({ ...metricRules, groups: updatedGroups });
    };

    const updateRule = (groupId: string, ruleId: string, updates: Partial<MetricRule>) => {
        const updatedGroups = metricRules.groups.map(group =>
            group.id === groupId
                ? {
                    ...group,
                    rules: group.rules.map(rule =>
                        rule.id === ruleId ? { ...rule, ...updates } : rule
                    )
                }
                : group
        );

        onRulesChange({ ...metricRules, groups: updatedGroups });
    };

    const toggleRule = (groupId: string, ruleId: string) => {
        onRulesChange({
            ...metricRules,
            groups: metricRules.groups.map(group =>
                group.id === groupId
                    ? {
                        ...group,
                        rules: group.rules.map(rule =>
                            rule.id === ruleId
                                ? { ...rule, enabled: !(rule.enabled ?? true) }
                                : rule
                        )
                    }
                    : group
            )
        });
    };

    const deleteRule = (groupId: string, ruleId: string) => {
        const updatedGroups = metricRules.groups.map(group =>
            group.id === groupId
                ? { ...group, rules: group.rules.filter(rule => rule.id !== ruleId) }
                : group
        );

        onRulesChange({ ...metricRules, groups: updatedGroups });
    };

    const deleteGroup = (groupId: string) => {
        onRulesChange({
            ...metricRules,
            groups: metricRules.groups.filter(group => group.id !== groupId)
        });
    };

    return (
        <Box sx={{ p: 0, mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Button
                    variant="outlined"
                    onClick={addRuleGroup}
                >
                    Add Rule Set
                </Button>
            </Box>

            {metricRules.groups.length > 1 && (
                <Box sx={{ mb: 3 }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Group Logic</InputLabel>
                        <Select
                            value={metricRules.groupLogic}
                            label="Group Logic"
                            onChange={(e) => onRulesChange({
                                ...metricRules,
                                groupLogic: e.target.value as 'AND' | 'OR'
                            })}
                        >
                            <MenuItem value="AND">AND</MenuItem>
                            <MenuItem value="OR">OR</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            )}

            {metricRules.groups.map((group, groupIndex) => (
                <Card key={group.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                            <NumbersIcon sx={{ mr: 1 }} /> {/* Optional spacing */}
                            {groupIndex + 1}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            {group.rules.length > 1 && (
                                <FormControl size="small" sx={{ minWidth: 80 }}>
                                    <InputLabel>Logic</InputLabel>
                                    <Select
                                        value={group.logic}
                                        label="Logic"
                                        onChange={(e) => {
                                            const updatedGroups = metricRules.groups.map(g =>
                                                g.id === group.id ? { ...g, logic: e.target.value as 'AND' | 'OR' } : g
                                            );
                                            onRulesChange({ ...metricRules, groups: updatedGroups });
                                        }}
                                    >
                                        <MenuItem value="AND">AND</MenuItem>
                                        <MenuItem value="OR">OR</MenuItem>
                                    </Select>
                                </FormControl>
                            )}
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => addRule(group.id)}
                                sx={{ minWidth: 100 }}
                            >
                                Add Rule
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                color="error"
                                onClick={() => deleteGroup(group.id)}
                                sx={{ minWidth: 100 }}
                            >
                                Delete Set
                            </Button>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {group.rules.map((rule) => {
                            const isEnabled = rule.enabled ?? true;

                            return (
                                <Card sx={{ p: 2 }} variant="outlined">
                                    <Grid container key={rule.id} spacing={1} alignItems="center">
                                        <Grid item xs="auto">
                                            <Checkbox
                                                checked={isEnabled}
                                                onChange={() => toggleRule(group.id, rule.id)}
                                                inputProps={{ 'aria-label': 'Enable rule' }}
                                            />
                                        </Grid>

                                        <Grid item xs>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Field</InputLabel>
                                                <Select
                                                    value={rule.field}
                                                    label="Field"
                                                    onChange={(e) => updateRule(group.id, rule.id, { field: e.target.value })}
                                                >
                                                    {availableFields.map(field => (
                                                        <MenuItem key={field} value={field}>{field}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={1}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Operator</InputLabel>
                                                <Select
                                                    value={rule.operator}
                                                    label="Operator"
                                                    disabled={!isEnabled}
                                                    onChange={(e) => updateRule(group.id, rule.id, { operator: e.target.value as any })}
                                                >
                                                    {operatorOptions.map(op => (
                                                        <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={2}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                label="Threshold"
                                                value={rule.threshold}
                                                disabled={!isEnabled}
                                                inputProps={{ step: 0.01 }}
                                                onChange={(e) => updateRule(group.id, rule.id, { threshold: parseFloat(e.target.value) || 0 })}
                                            />
                                        </Grid>

                                        <Grid item xs={2}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                label="Allocation"
                                                value={rule.allocationFactor}
                                                disabled={!isEnabled}
                                                inputProps={{ step: 0.1 }}
                                                onChange={(e) => {
                                                    const val = parseFloat(e.target.value);
                                                    updateRule(group.id, rule.id, { allocationFactor: isNaN(val) ? 0 : val });
                                                }}
                                            />
                                        </Grid>

                                        <Grid item xs={1}>
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                                color="error"
                                                onClick={() => deleteRule(group.id, rule.id)}
                                            >
                                                Delete
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Card>
                            )
                        })}
                    </Box>
                </Card>
            ))}
        </Box>
    );
};